import { useLazyQuery }                                                                      from '@apollo/client'
import { format, parseISO, subDays }                                                         from 'date-fns'
import Papa                                                                                  from 'papaparse'
import { useEffect, useState }                                                               from 'react'
import { useDispatch }                                                                       from 'react-redux'
import { useNavigate, useSearchParams }                                                      from 'react-router-dom'
import Datepicker                                                                            from 'react-tailwindcss-datepicker'
import { setToasts, showToaster }                                                            from '../../features/toaster/toasterSlice'
import Pagination                                                                            from '../../components/Pagination/Pagination'
import { Button }                                                                            from '../../components/Button'
import { LoadingEllipsis }                                                                   from '../../components/Loaders'
import { GET_SHOP_ORDERS, GET_SHOP_ORDERS_TO_CSV, GET_MMA_TO_CSV, GET_PRODUCT_SALES_REPORT } from '../../graphql/shop'
import MainLayout                                                                            from '../../layouts/MainLayout'
import { exportData, formatGBP, formatTimeDate }                                             from '../../utilities'

export default function ShopOrders(): JSX.Element {
  const now: Date                                         = new Date
  const monthAgo: Date                                    = subDays( now, 30 )
  const navigate: any                                     = useNavigate()
  const dispatch: any                                     = useDispatch()
  const [ searchParams ]                                  = useSearchParams()
  const [ shopOrders, setShopOrders ]                     = useState( null )
  const [ fetchingFreeMMAToCSV, setFetchingFreeMMAToCSV ] = useState( false )
  const [ exportFromDate, setExportFromDate ]             = useState( { startDate: monthAgo, endDate: monthAgo } )
  const [ exportToDate, setExportToDate ]                 = useState( { startDate: now, endDate: now } )

  const [ getShopOrdersList, { loading } ] = useLazyQuery( GET_SHOP_ORDERS, {
    onCompleted: ( { shopOrdersList }: any ): void => setShopOrders( shopOrdersList ),
    onError:     ( error: any ): void => console.log( error )
  } )

  const [ getShopOrdersToCSV, { loading: fetchingOrdersToCSV } ]           = useLazyQuery( GET_SHOP_ORDERS_TO_CSV )
  const [ getMMATicketsToCSV, { loading: fetchingMMAToCSV } ]              = useLazyQuery( GET_MMA_TO_CSV )
  const [ getProductSalesReport, { loading: fetchingProductSalesReport } ] = useLazyQuery( GET_PRODUCT_SALES_REPORT )

  const fetchShopOrdersList: any = async ( page: number ): Promise<void> => {
    await getShopOrdersList( { variables: { input: { sortBy: 'created_at', page } } } )
    navigate( urlFormat( page ), { replace: true } )
  }

  const navigateToOrder = async ( orderId: string ): Promise<any> => {
    navigate( `/shop/order/${ orderId }` )
  }

  const urlFormat: any = ( page: number = 1 ): string => {
    let urlString: string = '/shop/orders'

    const params: any = new URLSearchParams()

    if ( page !== 1 ) {
      params.set( 'page', `${ page }` )
    } else {
      if ( params.has( 'page' ) ) params.delete( 'page' )
    }

    return params ? `${ urlString }?${ params }` : urlString
  }

  const handleDownloadProductSalesReport = async (): Promise<void> => {
    try {
      const { data } = await getProductSalesReport()

      if ( data?.productSalesReport?.length > 0 ) {
        const csvData = data.productSalesReport.map( ( item: any ) => ( {
          'Month':    item.month,
          'Product':  item.productName,
          'Quantity': item.quantity
        } ) )

        exportData(
          Papa.unparse( csvData ),
          `bmh_product_sales_report_${ format( new Date, 'dd-MM-yyyy-hh-mm' ) }.csv`,
          'text/csv;charset=utf-8;'
        )

        dispatchMessage( 'success', 'Report generated successfully' )
      } else {
        dispatchMessage( 'info', 'No sales found for 2025 yet.' )
      }
    } catch ( error ) {
      console.error( error )
      dispatchMessage( 'error', 'Failed to generate report' )
    }
  }

  const handleDownloadCsv = async (): Promise<void> => {
    const fromDate: string = new Date( exportFromDate.startDate ).toISOString()
    const toDate: string   = new Date( exportToDate.startDate ).toISOString()

    if ( fromDate <= toDate ) {
      const { data } = await getShopOrdersToCSV( { variables: { input: { fromDate, toDate } } } )

      if ( data?.shopOrdersToCSV?.length > 0 ) {
        // @ts-ignore
        const filteredData = data.shopOrdersToCSV.map( ( { __typename, updated_at, ...keep } ): any => {
          return {
            'Order Number':  keep.roi_id,
            'Date and Time': format( parseISO( keep.created_at ), 'dd-MM-yyyy hh:mm:ss' ),
            'Nursery Name':  keep.delivery_address.name,
            'VAT':           formatGBP( keep.total_vat ),
            'Delivery Cost': formatGBP( keep.delivery_cost ),
            'Total':         formatGBP( keep.total ),
            'Items':         JSON.stringify( keep.basket )
          }
        } )

        exportData(
          Papa.unparse( filteredData ),
          `shop_orders_${ format( new Date, 'dd-MM-yyyy-hh-mm-ss' ) }.csv`,
          'text/csv;charset=utf-8;'
        )
      } else {
        dispatchMessage( 'error', 'No orders found' )
      }
    }
  }

  const handleDownloadMMACsv = async ( free: boolean ): Promise<void> => {
    if ( free ) setFetchingFreeMMAToCSV( true )
    const date: Date = new Date

    const fromDate = new Date( date.getFullYear(), 0, 1, 0, 0, 0 ).toISOString()
    const toDate   = new Date( date.getFullYear(), 11, 31, 23, 59, 59 ).toISOString()

    const { data } = await getMMATicketsToCSV( { variables: { input: { fromDate, toDate, free } } } )

    const getQty: any = ( basket: any, free: boolean ): any => {
      let matched: any

      try {
        if ( free ) {
          matched = Object.values( basket ).filter( ( b: any ): any => b.name.includes( 'Midnight Moon Awards' ) && b.price === 0 )
        } else {
          matched = Object.values( basket ).filter( ( b: any ): any => b.name.includes( 'Midnight Moon Awards' ) && b.price > 0 )
        }

        // @ts-ignore
        return Object.values( matched[ 0 ].variants )[ 0 ].quantity
      } catch ( err ) {
        console.log( err )
        return 0
      }
    }

    if ( data?.getMMAToCSV?.length > 0 ) {
      // @ts-ignore
      const filteredData = data.getMMAToCSV.map( ( { __typename, updated_at, ...keep } ): any => {
        return {
          'Order Number':                                  keep.roi_id,
          [ `Total ${ free ? 'Free ' : '' }MMA Tickets` ]: getQty( keep.basket, free ),
          'Date and Time':                                 format( parseISO( keep.created_at ), 'dd-MM-yyyy hh:mm:ss' ),
          'Nursery Name':                                  keep.delivery_address.name
        }
      } )

      setFetchingFreeMMAToCSV( false )

      exportData(
        Papa.unparse( filteredData ),
        `mma_summary${ free ? '_free' : '' }_${ format( date, 'dd-MM-yyyy-hh-mm-ss' ) }.csv`,
        'text/csv;charset=utf-8;'
      )
    } else {
      dispatchMessage( 'error', 'No orders found' )
    }
  }

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  useEffect( (): any => {
    let shouldFetch: boolean = true
    const pageParam: number  = !isNaN( parseInt( searchParams.get( 'page' ) ) ) ? parseInt( searchParams.get( 'page' ) ) : 1

    if ( shouldFetch && !loading ) {
      fetchShopOrdersList( pageParam )
    }

    return (): void => {
      shouldFetch = false
    }
  }, [ searchParams ] )

  return (
    <MainLayout>
      <div className="max-w-screen-3xl mx-auto w-full">
        <div className="flex-grow lg:p-8 p-3 space-y-4 lg:space-y-8">
          { loading && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4 mt-4 w-full" /> }
          { !loading &&
            <div className="flex flex-col items-center w-full bg-white rounded-3xl p-4 lg:p-5">
              <div className="flex w-full mb-4">
                <header className="mb-4">
                  <h1 className="sm:text-2xl text-xl font-bold">Shop Orders</h1>
                </header>
              </div>

              <div className="w-full mx-auto">
                <div className="flex justify-end mb-4 space-x-4 h-[41px]">
                  <div className="flex">
                    <Button loading={ fetchingProductSalesReport } label="Export Product Sales Report 2025" onClick={ (): any => handleDownloadProductSalesReport() } />
                  </div>
                  <div className="flex">
                    <Button loading={ fetchingFreeMMAToCSV } label="Export MMA Free summary" onClick={ (): any => handleDownloadMMACsv( true ) } />
                  </div>
                  <div className="flex">
                    <Button loading={ fetchingMMAToCSV && !fetchingFreeMMAToCSV } label="Export MMA summary" onClick={ (): any => handleDownloadMMACsv( false ) } />
                  </div>
                </div>
                <div className="flex justify-end mb-4 space-x-4">
                  <div className="flex">
                    <Datepicker
                      useRange={ false }
                      asSingle={ true }
                      value={ exportFromDate }
                      onChange={ ( value: any ) => setExportFromDate( value ) }
                    />
                  </div>

                  <div className="flex">
                    <Datepicker
                      useRange={ false }
                      asSingle={ true }
                      value={ exportToDate }
                      onChange={ ( value: any ) => setExportToDate( value ) }
                    />
                  </div>

                  <div className="flex min-w-32">
                    <Button loading={ fetchingOrdersToCSV } label="Export Orders" onClick={ handleDownloadCsv } />
                  </div>
                </div>
                <div className="bg-white overflow-hidden rounded-2xl shadow-sm ring-1 ring-gray-200 relative z-0">
                  <div className="overflow-x-auto">
                    <table className="min-w-full table-fixed">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Order ID</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Order Date</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Transaction ID</th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-600">Subtotal</th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-600">VAT</th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-600">Delivery</th>
                          <th scope="col" className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-gray-600">Total</th>
                          {/*<th scope="col" className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Status</th>*/ }
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600">User</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-600">Location</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-100">
                        {
                          shopOrders?.data?.map( ( order: any ): any =>
                                                   <tr className="hover:bg-gray-50 cursor-pointer" key={ order.id } onClick={ (): any => navigateToOrder( order.id ) }>
                                                     <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">#{ order.roi_id }</td>
                                                     <td className="px-4 py-3 text-gray-700">{ formatTimeDate( order.created_at ) }</td>
                                                     <td className="px-4 py-3 text-gray-700">{ order.transaction_id }</td>
                                                     <td className="px-4 py-3 text-right tabular-nums">{ formatGBP( order.subtotal ) }</td>
                                                     <td className="px-4 py-3 text-right tabular-nums">{ formatGBP( order.total_vat ) }</td>
                                                     <td className="px-4 py-3 text-right tabular-nums">{ formatGBP( order.delivery_cost ) }</td>
                                                     <td className="px-4 py-3 text-right font-semibold tabular-nums">{ formatGBP( order.total ) }</td>
                                                     {/*<td className="px-4 py-3">*/ }
                                                     {/*<span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-200">*/ }
                                                     {/*  <span className="h-1.5 w-1.5 rounded-full bg-amber-500"></span> New*/ }
                                                     {/*</span>*/ }

                                                     {/*<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">*/ }
                                                     {/*  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span> Paid*/ }
                                                     {/*</span>*/ }
                                                     {/*<span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-200">*/ }
                                                     {/*  <span className="h-1.5 w-1.5 rounded-full bg-rose-500"></span> Refunded*/ }
                                                     {/*</span>*/ }
                                                     {/*</td>*/ }
                                                     <td className="px-4 py-3">{ order.user.name }</td>
                                                     <td className="px-4 py-3">{ order.delivery_address.name }</td>
                                                   </tr>
                          )
                        }
                      </tbody>

                      {/*<tfoot className="bg-gray-50">*/ }
                      {/*  <tr>*/ }
                      {/*    <th scope="row" colspan="3" className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Totals</th>*/ }
                      {/*    <td className="px-4 py-3 text-right font-medium tabular-nums">$254.00</td>*/ }
                      {/*    <td className="px-4 py-3 text-right font-medium tabular-nums">$20.32</td>*/ }
                      {/*    <td className="px-4 py-3 text-right font-medium tabular-nums">$9.80</td>*/ }
                      {/*    <td className="px-4 py-3 text-right font-semibold tabular-nums">$284.12</td>*/ }
                      {/*    <td className="px-4 py-3" colspan="3"></td>*/ }
                      {/*  </tr>*/ }
                      {/*</tfoot>*/ }
                    </table>
                  </div>
                </div>
              </div>

              { ( shopOrders?.links?.totalPages > 0 ) &&
                <Pagination
                  className="justify-content-center pb-2 lg:pb-3"
                  currentPage={ shopOrders.links.page }
                  totalCount={ shopOrders.links.totalResults }
                  pageSize={ shopOrders.links.perPage }
                  onPageChange={ ( page: number ): Promise<void> => fetchShopOrdersList( page ) }
                /> }
            </div>
          }
        </div>
      </div>
    </MainLayout>
  )
}
